import { Button, Message } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-regular-svg-icons";

import { DEFAULT_ERROR_MESSAGE } from "../../../../../../constants/errors/errors";
import { useToasts } from "react-toast-notifications";
import { UpdateSubscriberPreferencesToast } from "../../../../components/UpdateSubscriberPreferencesToast";
import { useNotifySubscriptionPreferencesUpdate } from "../../../../hooks/useNotifySubscriptionPreferencesUpdate";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "../../../../hooks/useInfiniteNotifySubscriberContactMethods";
import { useQueryCache } from "react-query";
import { buildNotifySubscriberContactMethodsQueryKey } from "../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { buildNotifyContentDetailsQueryKey } from "../../../../hooks/useNotifyContent";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import identitySelectors from "../../../../../../selectors/identity";
import { useAnonymousToken } from "../../../../../auth/hooks/useAnonymousToken";
import { AddSubscriberContactMethodModal } from "../subscription-groups/components/modals/AddSubscriberContactMethodModal";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./NotifyContentSubscribeBanner.scss";

import { OTPModalData, VerifyOTPModal } from "../subscription-groups/components/modals/VerifyOTPModal";
import { useAuthOTPSend } from "../../../../../auth/hooks/useAuthOTPSend";

import { useNotifySubscriptionPreferenceUpdate } from "../../../../hooks/useNotifySubscriptionPreferenceUpdate";
import { useAuthIssueAnonymousTokenByOTP } from "../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTP";

interface SubscriptionGroup {
  id: number;
  name: string;
  visibility?: string | undefined;
  cityId: number;
  autoSubscribe: boolean;
}

export function NotifyContentSubscribeBanner(props: {
  unSubscribedGroups: SubscriptionGroup[] | undefined;
  subscriberContactMethodIds: number[] | undefined;
}) {
  const { addToast } = useToasts();
  const queryCache = useQueryCache();

  const params = useParams<{ site: string; id: string }>();
  //query string params

  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);
  const { isLoggedIn: isAnonymouslyLoggedIn } = useAnonymousToken();

  const queryStringParams = new URLSearchParams(window.location.search);
  const action = queryStringParams.get("action");
  const groupId = queryStringParams.get("groupId");

  const [hasProcessedQueryParams, setHasProcessedQueryParams] = useState(false);

  const [isContactMethodModalOpen, setIsContactMethodModalOpen] = useState(false);
  const [subscriptionGroupIdClickedOn, setSubscriptionGroupIdClickedOn] = useState<number | null>(null);
  const [otpModalData, setOTPModalData] = useState<OTPModalData | null>(null);

  const { sendAuthOTP, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: (data) => {
      setOTPModalData({
        identifier: data.identifier,
        type: data.type,
      });
      setIsContactMethodModalOpen(false);
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });

  const { issueAnonymousTokenByOTP, isAuthIssueAnonymousTokenLoading } = useAuthIssueAnonymousTokenByOTP({
    onSuccess: (newSubscriberContactMethodId) => {
      if (subscriptionGroupIdClickedOn) {
        handleSubscribeContactMethod(subscriptionGroupIdClickedOn, newSubscriberContactMethodId);
      }
      setOTPModalData(null);
      setIsContactMethodModalOpen(false);
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });

  const { editSubscriptionPreferences: bulkEditSubscriptionPreferences } = useNotifySubscriptionPreferencesUpdate({
    onSuccess: async (res) => {
      // its bulk so they should all be same
      addToast(
        <UpdateSubscriberPreferencesToast
          groupName={res[0].subscriptionGroup.name}
          handleUndo={() => handleUnsubscribeContactMethod(res[0].subscriptionGroupId)}
          type={res[0].type}
        />,
        {
          appearance: "success",
          autoDismiss: true,
        },
      );
      queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
      queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());
      queryCache.invalidateQueries(buildNotifyContentDetailsQueryKey({ id: Number(params.id) }));
    },
    onError: (error: any) => {
      const errorMsg = error?.error?.message ?? DEFAULT_ERROR_MESSAGE;
      addToast(errorMsg, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });

  const { editSubscriptionPreference } = useNotifySubscriptionPreferenceUpdate({
    onSuccess: async (res) => {
      await addToast(
        <UpdateSubscriberPreferencesToast
          groupName={res.subscriptionGroup.name}
          handleUndo={() => handleUnsubscribeContactMethod(res.subscriptionGroupId, res.subscriberContactMethodId)}
          type={res.type}
        />,
        {
          appearance: "success",
          autoDismiss: true,
        },
      );
    },
  });

  const handleUnsubscribeContactMethod = async (
    subscriptionGroupId: number,
    newAnonymousSubscriberContactMethodId?: number,
  ) => {
    if (props.subscriberContactMethodIds || newAnonymousSubscriberContactMethodId) {
      if (isLoggedIn) {
        await bulkEditSubscriptionPreferences({
          subscriberContactMethodIds: props.subscriberContactMethodIds ?? [],
          subscriptionGroupIds: [subscriptionGroupId],
          type: "Unsubscribe",
          overwriteExistingPreference: true,
        });
      } else if (isAnonymouslyLoggedIn()) {
        await editSubscriptionPreference({
          subscriberContactMethodId: newAnonymousSubscriberContactMethodId ?? 0,
          subscriptionGroupId: subscriptionGroupId,
          type: "Unsubscribe",
        });
      } else {
        setIsContactMethodModalOpen(true);
      }
    }
  };

  const handleSubscribeContactMethod = async (groupId: number, newSubscriberContactMethodId?: number) => {
    if (isLoggedIn) {
      await bulkEditSubscriptionPreferences({
        subscriberContactMethodIds: props.subscriberContactMethodIds ?? [],
        subscriptionGroupIds: [groupId],
        type: "Subscribe",
        overwriteExistingPreference: true,
      });
    } else if (isAnonymouslyLoggedIn()) {
      await editSubscriptionPreference({
        subscriberContactMethodId: props.subscriberContactMethodIds?.[0] ?? newSubscriberContactMethodId ?? 0,
        subscriptionGroupId: groupId,
        type: "Subscribe",
      });
    } else {
      setIsContactMethodModalOpen(true);
    }

    setSubscriptionGroupIdClickedOn(null);
  };

  useEffect(() => {
    // This should make it run at max one time
    if (
      !hasProcessedQueryParams &&
      isLoggedIn &&
      action === "subscribe" &&
      groupId &&
      props.subscriberContactMethodIds &&
      props.subscriberContactMethodIds.length > 0
    ) {
      const groupIdNum = Number(groupId);
      if (!isNaN(groupIdNum)) {
        handleSubscribeContactMethod(groupIdNum);
        setHasProcessedQueryParams(true);
      }
    }
  }, [action, groupId, isLoggedIn, props.subscriberContactMethodIds, hasProcessedQueryParams]);

  return (
    <>
      <VerifyOTPModal
        data={otpModalData}
        onClose={() => {
          setOTPModalData(null);
          setIsContactMethodModalOpen(false);
        }}
        onChangeIdentifier={() => {
          setOTPModalData(null);
          setIsContactMethodModalOpen(true);
        }}
        verifyOTPMutation={{
          isLoading: isAuthIssueAnonymousTokenLoading,
          mutate: async ({ identifier, type, otp }) => {
            await issueAnonymousTokenByOTP({
              identifier,
              type,
              otp,
            });
          },
        }}
      />
      <AddSubscriberContactMethodModal
        open={isContactMethodModalOpen}
        onClose={() => {
          setIsContactMethodModalOpen(false);
        }}
        signInRedirectURL={`?redirect=${encodeURIComponent(
          `/${params.site}/public/notify/posts/${params.id}?action=subscribe&groupId=${subscriptionGroupIdClickedOn}`,
        )}`}
        onSubmit={sendAuthOTP}
        submitTitle='Subscribe'
        isLoading={isAuthOTPSendLoading}
      />
      <Message
        className='tw-min-w-3xl tw-flex tw-max-w-3xl tw-flex-col tw-justify-between tw-gap-3 sm:tw-flex-row'
        info
      >
        <div className='tw-flex tw-items-center tw-gap-6'>
          <FontAwesomeIcon icon={faBell} size='xl' />
          <div className='tw-flex tw-flex-col'>
            <div>
              <Message.Header>
                {props.unSubscribedGroups?.length == 1 ? "Stay Informed!" : "Subscribe for More Updates!"}
              </Message.Header>
              <Message.Content>
                {props.unSubscribedGroups?.length == 1
                  ? "Subscribe to groups related to this post to see more updates like this"
                  : "Check out some more topics you might be interested in. You'll need to sign in or verify your contact method first"}
              </Message.Content>
            </div>

            <div>
              <TransitionGroup component={null}>
                {props.unSubscribedGroups?.map((group) => (
                  <CSSTransition key={group.id} timeout={800} classNames='button'>
                    <Button
                      className='tw-mx-1 tw-rounded-md tw-border tw-border-[0.5px] tw-border-solid tw-border-[#2185D0] tw-bg-transparent tw-px-4 tw-py-3 tw-text-[#2185D0]'
                      onClick={async () => {
                        if (isLoggedIn || isAnonymouslyLoggedIn()) {
                          setSubscriptionGroupIdClickedOn(group.id);
                          await handleSubscribeContactMethod(group.id);
                        } else {
                          setSubscriptionGroupIdClickedOn(group.id);
                          setIsContactMethodModalOpen(true);
                        }
                      }}
                    >
                      {group.name}
                    </Button>
                  </CSSTransition>
                ))}
              </TransitionGroup>
              {props.unSubscribedGroups && props.unSubscribedGroups.length > 3 && (
                <span className='tw-ml-2 tw-font-bold tw-text-[#2185D0]'>
                  <Link
                    to={`/${params.site}/public/notify/subscription-groups`}
                    className='tw-text-[#2185D0] tw-no-underline'
                  >
                    View all
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
      </Message>
    </>
  );
}
